import { ReactPlugin } from '@microsoft/applicationinsights-react-js'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import LogRocket from 'logrocket'
import { Profile } from 'model/User'
import { store } from 'store/configureStore'

export class SegmentService {
  private static currentInstance: SegmentService
  private partyId: string
  private email: string
  private currentPage: string

  public static getInstance() {
    if (!SegmentService.currentInstance) {
      SegmentService.currentInstance = new SegmentService()
    }
    return SegmentService.currentInstance
  }

  private constructor() {
    if (process.env.REACT_APP_SEGMENT_KEY) {
      window.analytics.load(process.env.REACT_APP_SEGMENT_KEY)
    }
  }

  public page(name: string) {
    if (this.currentPage === name) return

    this.currentPage = name

    window.analytics.page(name, {
      partyId: this.partyId,
      email: this.email,
    })
  }

  public identify(userId: string, email: string, partyId: string) {
    window.analytics.identify(userId, {
      email: email,
      partyId: partyId,
    })

    LogRocket.track('workspace_selected', { partyId: partyId })

    this.partyId = partyId
    this.email = email
  }
}

export class TelemetryService {
  private static currentInstance: TelemetryService

  public readonly reactPlugin: ReactPlugin

  private appInsights: ApplicationInsights

  public static getInstance() {
    if (!TelemetryService.currentInstance)
      TelemetryService.currentInstance = new TelemetryService()

    return this.currentInstance
  }

  private constructor() {
    this.reactPlugin = new ReactPlugin()
  }

  initialize() {
    const instrumentationKey = process.env.REACT_APP_INSTRUMENTATION_KEY

    if (!instrumentationKey) return null

    const environment = process.env.REACT_APP_ENVIRONMENT || 'local'

    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: instrumentationKey,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        extensions: [this.reactPlugin as any],
        enableAutoRouteTracking: true,
        appId: `FrontEnd - ${environment}`,
        disableTelemetry: true,
      },
    })

    TelemetryService.getInstance().appInsights?.loadAppInsights()
    TelemetryService.getInstance().appInsights?.addTelemetryInitializer(
      (item) => {
        item.tags['ai.cloud.role'] = 'FrontEnd'
        item.tags['ai.cloud.roleInstance'] = `FrontEnd - ${environment}`
      }
    )
  }

  public logError(error: unknown) {
    try {
      const appState = store.getState()
      TelemetryService.getInstance()?.appInsights?.trackException({
        exception: error as Error,
        properties: {
          page: window.location.pathname + window.location.search,
        },
      })
      LogRocket.captureException(error as Error, {
        extra: {
          partyId: appState?.user?.organizationContext?.id,
          partyName: appState?.user?.organizationContext?.name,
          tenantId: appState?.user?.tenantId,
        },
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  public IdentifyUser(user: Partial<Profile>) {
    if (!user) {
      return
    }

    try {
      this.appInsights?.setAuthenticatedUserContext(user.id, user.id, true)

      LogRocket.identify(user.email, {
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
      })
    } catch (err) {
      console.error('unable setup telemetry for user', user, err)
      LogRocket.captureException(err as Error)
    }
  }

  public TrackEvent(
    eventName: string,
    properties?: Record<
      string,
      string | number | boolean | string[] | number[] | boolean[]
    >
  ) {
    LogRocket?.track(eventName, properties)
  }
}
